import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Snackbar, Typography, Link, TextField, Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SetupAccountLayout } from './SetupAccountFlow';
import useAuth, { useAuthEffects } from '../components/Auth';
import { useActivateVendorProfileMutation, useGetCompanyVendorProfileQuery } from '../services/vendorProfile';
import { useUpdateMyCompanyMutation } from '../services/accounts';
import { Trans, useTranslation } from 'react-i18next';
import { Currencies } from '../types';
import slugify from 'slugify';
import { useAppSelector } from '../hooks';
import { selectMyCompany } from './accountSlice';

/**
 * Back-end dto:
 * @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  description: string;

  @IsString()
  @IsNotEmpty()
  address: string;

  @IsString()
  @IsNotEmpty()
  legalName: string;

  @IsString()
  taxId: string;

  @IsString()
  @IsNotEmpty()
  receiveOrdersEmail: string;

  @IsString()
  website: string;

  @IsString()
  vendorContactEmail: string;

  @IsString()
  @IsNotEmpty()
  currency: string; 
 *
 */

const CompleteRegistrationVendor = () => {
	useAuthEffects();
	const [activateVendorProfile] = useActivateVendorProfileMutation();
	const [profileId, setProfileId] = useState('');
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [updateMyCompanyMutation] = useUpdateMyCompanyMutation();
	const {logout, userProfile} = useAuth();
	const myCompany = useAppSelector(selectMyCompany);
	const {data: existingVendorProfile, isLoading: isExistingVendorProfileLoading} = useGetCompanyVendorProfileQuery(undefined, { skip: !userProfile });
	const navigate = useNavigate();
	const {t} = useTranslation();

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		if (!myCompany) {
			return;
		}

		updateMyCompanyMutation({
			name: formData.get('name') as string,
			...(!myCompany.profileId ? {profileId: profileId} : {}),
		}).unwrap().then(() => {
			activateVendorProfile({
				legalName: formData.get('legalName') as string,
				address: formData.get('address') as string,
				currency: formData.get('currency') as Currencies,
			}).unwrap().then(() => {
				navigate('/profile');
			}).catch((error) => {
				const errorMessage = (error as any)?.data?.message;
				setErrorMessage(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
			});
		}).catch((error) => {
			const errorMessage = (error as any)?.data?.message;
			setErrorMessage(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
		});
	};
	
	const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setProfileId(slugify(e.target.value, {lower: true, strict: true, locale: 'en', trim: true}));
	};
	
	useEffect(() => {
		if (existingVendorProfile) {
			navigate('/profile');
		}
	}, [existingVendorProfile]);

	const isProfileLoading = !myCompany || isExistingVendorProfileLoading;

	return <SetupAccountLayout><Box display='flex' justifyContent='center' alignItems='center' flexDirection='column' gap={2}>
		<Typography variant='body1' textAlign='center' py={1}>Activate your vendor profile to start listing products in your store.</Typography>
		{!isProfileLoading && <form onSubmit={handleSubmit}>
			<Box display='flex' flexDirection='column' gap={2}>
				<div>
					<TextField fullWidth required autoFocus autoComplete='off' label={t('signUpVendor.form.name')} name='name' onChange={handleCompanyNameChange} defaultValue={myCompany?.name} />
				</div>
				<FormControl fullWidth>
					<InputLabel id={`${String('currency')}-label`}>{t('signUpVendor.form.currency')}</InputLabel>
					<Select fullWidth required autoComplete='off' name='currency' label={t('signUpVendor.form.currency')} defaultValue={Currencies.EUR}>
						<MenuItem value={Currencies.EUR}>EUR</MenuItem>
						<MenuItem value={Currencies.USD}>USD</MenuItem>
					</Select>
				</FormControl>
				<Typography variant='caption' color='text.secondary'>The following legal information will be used in the receipts and invoices.</Typography>
				<div>
					<TextField fullWidth required autoComplete='off' label={t('signUpVendor.form.legalName')} name='legalName' />
				</div>
				<TextField fullWidth required autoComplete='off' label={t('signUpVendor.form.address')} name='address' />
				<FormControlLabel sx={{userSelect: 'none'}} control={<Checkbox />} required label={<Trans i18nKey="signUpVendor.termsAndConditions" components={{ 
					termsOfServiceLink: <Link href={t('common.termsOfServiceHref')} target="_blank" />,
					privacyPolicyLink: <Link href={t('common.privacyPolicyHref')} target="_blank" />
				}} />} />
				<Button size='large' variant='contained' color='primary' type='submit'>{t('signUpVendor.getStarted')}</Button>
			</Box>
		</form>}
		<Box py={4} textAlign='center'>
			<Button onClick={() => logout()}>Log out</Button>
		</Box>
		<Snackbar
			open={!!errorMessage}
			autoHideDuration={6000}
			onClose={() => setErrorMessage(null)}
		>
			<Alert severity="error">{errorMessage}</Alert>
		</Snackbar>
	</Box>
	</SetupAccountLayout>;
};

export default CompleteRegistrationVendor;